'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, LogIn, Film, Award, Star, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const cinemaQuotes = [
  { quote: 'Cinema is a mirror by which we often see ourselves.', author: 'Martin Scorsese' },
  { quote: 'Every frame is a painting. Every story is a life.', author: 'Motion Max Awards' },
  { quote: 'The camera is an instrument that teaches people how to see without a camera.', author: 'Dorothea Lange' },
];

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { signIn } = useAuth();
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signIn(email, password);
      toast.success('Welcome back! Signing you in...');
      router.push('/contestant-dashboard');
    } catch (error: any) {
      toast.error(error?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const quote = cinemaQuotes[1];

  return (
    <div className="min-h-screen flex">
      {/* Left: Cinematic visual panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <Image
          src="https://img.rocket.new/generatedImages/rocket_gen_img_17c0de821-1772183672598.png"
          alt="Cinematic film production set with professional camera equipment and dramatic lighting"
          fill
          priority
          className="object-cover"
          sizes="50vw"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/55 to-black/75" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 30% 70%, rgba(201,168,76,0.15) 0%, transparent 60%)' }} />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link href="/" className="flex items-center gap-3 group">
            <ArrowLeft size={16} className="text-white/50 group-hover:text-primary transition-colors" />
            <div className="w-10 h-10 rounded-xl gradient-gold flex items-center justify-center font-extrabold text-primary-foreground text-lg">M</div>
            <span className="font-extrabold text-xl text-white">Motion Max Awards</span>
          </Link>

          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3 mb-8">
              {[
                { icon: <Film size={16} />, label: '18 Categories' },
                { icon: <Award size={16} />, label: '₹5L+ Prizes' },
                { icon: <Star size={16} />, label: '50+ Judges' },
              ]?.map((item) => (
                <div key={item?.label} className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)' }}>
                  <span className="text-primary">{item?.icon}</span>
                  <span className="text-white text-xs font-semibold">{item?.label}</span>
                </div>
              ))}
            </div>

            <div className="glass-card rounded-2xl p-6" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(201,168,76,0.2)' }}>
              <p className="text-white/90 text-lg font-medium leading-relaxed italic mb-3">&ldquo;{quote?.quote}&rdquo;</p>
              <p className="text-primary text-sm font-semibold">— {quote?.author}</p>
            </div>

            <p className="text-white/50 text-sm">India&apos;s Premier Media Awards Platform · Season 3, 2026</p>
          </div>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl gradient-gold flex items-center justify-center font-extrabold text-primary-foreground">M</div>
              <span className="font-extrabold text-lg gradient-gold-text">Motion Max Awards</span>
            </div>
            <Link href="/" className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
              <ArrowLeft size={12} /> Back
            </Link>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-extrabold text-foreground mb-2" style={{ letterSpacing: '-0.025em' }}>Welcome Back</h2>
            <p className="text-sm text-muted-foreground">
              New contestant?{' '}
              <Link href="/register" className="text-primary hover:text-primary-light font-semibold transition-colors">
                Create your account
              </Link>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-dark w-full px-4 py-3.5 rounded-xl text-sm"
                placeholder="your@email.com"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Password</label>
                <button type="button" className="text-xs text-primary hover:text-primary-light font-semibold transition-colors">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-dark w-full px-4 py-3.5 pr-12 rounded-xl text-sm"
                  placeholder="Your password"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <input type="checkbox" id="remember" className="w-4 h-4 rounded accent-primary" />
              <label htmlFor="remember" className="text-xs text-muted-foreground font-medium cursor-pointer">Remember me for 30 days</label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <><Loader2 size={16} className="animate-spin" /> Signing in...</>
              ) : (
                <><LogIn size={16} /> Sign In to Motion Max</>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gold-subtle text-center">
            <p className="text-xs text-muted-foreground mb-4">Don&apos;t have an account yet?</p>
            <Link href="/register" className="btn-outline-gold w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2">
              <Award size={16} /> Create Account — It&apos;s Free
            </Link>
          </div>

          <p className="text-center text-xs text-muted-foreground/50 mt-6">
            Protected by enterprise-grade security. Your data is safe.
          </p>
        </div>
      </div>
    </div>
  );
}